import {
  Button, DeleteButton, EditButton, SaveButton, SimpleForm,
  TextField, Toolbar, TopToolbar, useDataProvider, useNotify, useRefresh, useTranslate, useResourceContext, useRedirect,
  useListContext,
} from 'react-admin';
import { Datagrid, IfCanAccess } from '@react-admin/ra-rbac';
import { ListLive } from '@react-admin/ra-realtime';
import { required, useGetList, useRecordContext } from 'ra-core';
import { useNavigate } from 'react-router-dom';
import {
  Typography, Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText,
  TableContainer, Table, TableHead, TableBody, TableRow, TableCell, Checkbox, CircularProgress,
  Grid,
  IconButton,
  useTheme,
  Tooltip,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import { Close } from '@mui/icons-material';
import { SecurityInventoryCategory, ExpiryTimeGranularityType } from '@x-guard/xgac-types/xgac';
import { useDebounce } from 'use-debounce';
import { LIST_DEBOUNCE } from '../../../config';
import SearchFields from '../components/fields/Filters';
import { TimeGranularityField } from '../../../components/fields/TimeGranularityField';
import { EditWithSuccess } from '../../../components/baseForms/EditWithSuccess';
import { GrayTextInputWithUpperLabel } from '../components/inputs/GrayTextInputWithUpperLabel';
import { AssetsInDialogButton } from '../../../components/buttons/assetsInDialogButton';
import { NotifiedPeopleInput } from '../components/inputs/NotifiedPeopleInput';
import { GrayExpiryTimeGranularityInputWithUpperLabel } from '../components/inputs/GrayExpiryTimeGranularityInputWithUpperLabel';
import { IconSelector } from '../components/inputs/IconSelector';
import { IconField } from '../components/fields/IconField';
import { InventoryCategoryIconName } from '../components/icons/inventoryCategoryIcons';
import { CreateWithSuccess } from '../../../components/baseForms/CreateWithSuccess';
import { CustomReminderPeriodInput } from '../components/inputs/CustomReminderPeriodInput';
import { securityItemsResource } from './inventoryItems/util';
import { SearchInput } from '../components/SearchInput';
import { DEFAULT_REMINDER_PERIODS, getExpiryStatus } from '../../../utils/certificationsAndItemsExpiry';
import { AddCategoryDialog } from '../components/AddCategoryDialog';
import { xgacDataProvider } from '../../../dataProviders/xgacDataProvider';

type InventoryCategoryPresetKey =
  | 'fire_extinguishers'
  | 'fire_doors'
  | 'fire_dampers_ventilation'
  | 'fire_alarm_system'
  | 'fire_hose_reels'
  | 'emergency_lighting'
  | 'evacuation_alarm_system'
  | 'smoke_detectors'
  | 'sprinkler_system'
  | 'escape_route_signage'
  | 'aed_automated_external_defibrillator'
  | 'first_aid_kits'
  | 'co2_carbon_monoxide_detectors'
  | 'gas_detection_systems'
  | 'evacuation_plans'
  | 'ppe_personal_protective_equipment'
  | 'eyewash_station'
  | 'emergency_shower';

type InventoryCategoryPreset = {
  presetKey: InventoryCategoryPresetKey;
  icon: InventoryCategoryIconName;
  expiryInterval: SecurityInventoryCategory['expiryInterval'];
};

type InventoryCategoryCounts = {
  validWithExpiring: number;
  expiring: number;
  expired: number;
};

const PRESET_I18N_PREFIX = 'resources.security-inventory-items.presets';

const categoryPresets: InventoryCategoryPreset[] = [
  {
    presetKey: 'fire_extinguishers',
    icon: 'fire-extinguisher',
    expiryInterval: { type: ExpiryTimeGranularityType.Year, value: 1 },
  },
  {
    presetKey: 'fire_doors',
    icon: 'meeting-room',
    expiryInterval: { type: ExpiryTimeGranularityType.Year, value: 1 },
  },
  {
    presetKey: 'fire_dampers_ventilation',
    icon: 'air',
    expiryInterval: { type: ExpiryTimeGranularityType.Year, value: 1 },
  },
  {
    presetKey: 'fire_alarm_system',
    icon: 'sensors',
    expiryInterval: { type: ExpiryTimeGranularityType.Year, value: 1 },
  },
  {
    presetKey: 'fire_hose_reels',
    icon: 'water-drop',
    expiryInterval: { type: ExpiryTimeGranularityType.Year, value: 1 },
  },
  {
    presetKey: 'emergency_lighting',
    icon: 'wb-iridescent',
    expiryInterval: { type: ExpiryTimeGranularityType.Year, value: 1 },
  },
  {
    presetKey: 'evacuation_alarm_system',
    icon: 'campaign',
    expiryInterval: { type: ExpiryTimeGranularityType.Year, value: 1 },
  },
  {
    presetKey: 'smoke_detectors',
    icon: 'smoke-free',
    expiryInterval: { type: ExpiryTimeGranularityType.Year, value: 1 },
  },
  {
    presetKey: 'sprinkler_system',
    icon: 'water',
    expiryInterval: { type: ExpiryTimeGranularityType.Year, value: 1 },
  },
  {
    presetKey: 'escape_route_signage',
    icon: 'exit-to-app',
    expiryInterval: { type: ExpiryTimeGranularityType.Year, value: 1 },
  },
  {
    presetKey: 'aed_automated_external_defibrillator',
    icon: 'monitor-heart',
    expiryInterval: { type: ExpiryTimeGranularityType.Year, value: 1 },
  },
  {
    presetKey: 'first_aid_kits',
    icon: 'medical-services',
    expiryInterval: { type: ExpiryTimeGranularityType.Month, value: 6 },
  },
  {
    presetKey: 'co2_carbon_monoxide_detectors',
    icon: 'sensors',
    expiryInterval: { type: ExpiryTimeGranularityType.Year, value: 1 },
  },
  {
    presetKey: 'gas_detection_systems',
    icon: 'sensors',
    expiryInterval: { type: ExpiryTimeGranularityType.Year, value: 1 },
  },
  {
    presetKey: 'evacuation_plans',
    icon: 'map',
    expiryInterval: { type: ExpiryTimeGranularityType.Year, value: 1 },
  },
  {
    presetKey: 'ppe_personal_protective_equipment',
    icon: 'health-and-safety',
    expiryInterval: { type: ExpiryTimeGranularityType.Year, value: 1 },
  },
  {
    presetKey: 'eyewash_station',
    icon: 'visibility',
    expiryInterval: { type: ExpiryTimeGranularityType.Month, value: 6 },
  },
  {
    presetKey: 'emergency_shower',
    icon: 'shower',
    expiryInterval: { type: ExpiryTimeGranularityType.Month, value: 6 },
  },
];

const presetToCreatePayload = (
  preset: InventoryCategoryPreset,
  translate: (key: string) => string,
): Omit<SecurityInventoryCategory, '_id' | 'customer' | 'share' | 'createdAt' | 'updatedAt'> => ({

  name: translate(`${PRESET_I18N_PREFIX}.${preset.presetKey}.name`),
  description: translate(`${PRESET_I18N_PREFIX}.${preset.presetKey}.description`),
  icon: preset.icon,
  expiryInterval: preset.expiryInterval,
  notifiedPeople: {
    peopleToNotify: [],
    emails: [],
  },
  reminderPeriods: DEFAULT_REMINDER_PERIODS,
});

const InventoryCategoryCreateFromPresetsDialog = (props: { open: boolean; onClose: () => void }) => {

  const [selectedItems, setSelectedItems] = useState<InventoryCategoryPresetKey[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm] = useDebounce(searchTerm, LIST_DEBOUNCE);
  const [inventoryCategories, setInventoryCategories] = useState<SecurityInventoryCategory[]>([]);

  const dataProvider = useDataProvider();
  const resource = useResourceContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const translate = useTranslate();

  useEffect(() => {

    if (!props.open) return;

    xgacDataProvider.getList('security-inventory-categories', {
      pagination: { page: 1, perPage: -1 },
    }).then(({ data }) => {

      setInventoryCategories(data);

    });

  }, [props.open]);

  const existingCategoryNames = useMemo(() => {

    return new Set(inventoryCategories.map((row) => row.name));

  }, [inventoryCategories]);

  const handleClose = () => {

    setSelectedItems([]);
    props.onClose();

  };

  const handleToggleSelect = (presetKey: InventoryCategoryPresetKey, isDisabled: boolean) => {

    if (isDisabled) return;

    setSelectedItems((current) => {

      if (current.includes(presetKey)) return current.filter((item) => item !== presetKey);
      return [...current, presetKey];

    });

  };

  const handleAdd = async () => {

    setIsLoading(true);

    try {

      const selected = categoryPresets.filter((row) => selectedItems.includes(row.presetKey));

      if (selected.length === 0) {

        return;

      }

      await Promise.all(
        selected.map((item) => dataProvider.create(
          resource || 'security-inventory-categories',
          { data: presetToCreatePayload(item, translate) },
        )),
      );

      notify(`${translate('resources.security-inventory-categories.text.title_single')} ${translate('general.text.saved').toLowerCase()}`, { type: 'success' });
      refresh();

      handleClose();

    } catch (error) {

      if (error instanceof Error) {

        notify(error.message, { type: 'error' });

      }

    } finally {

      setIsLoading(false);

    }

  };

  const filteredCategoryPresets = useMemo(() => {

    const q = debouncedSearchTerm.trim().toLowerCase();
    if (!q) return categoryPresets;

    return categoryPresets.filter((row) => {

      const name = translate(`${PRESET_I18N_PREFIX}.${row.presetKey}.name`).toLowerCase();
      const description = translate(`${PRESET_I18N_PREFIX}.${row.presetKey}.description`).toLowerCase();
      return name.includes(q) || description.includes(q);

    });

  }, [debouncedSearchTerm, translate]);

  const mappedPresets = useMemo(() => {

    return filteredCategoryPresets
      .map((row) => ({
        ...row,
        exists: existingCategoryNames.has(translate(`${PRESET_I18N_PREFIX}.${row.presetKey}.name`)),
      }))
      .sort((a, b) => {

        if (a.exists && !b.exists) return 1;
        if (!a.exists && b.exists) return -1;
        return 0;

      });

  }, [filteredCategoryPresets, existingCategoryNames, translate]);

  const showEmptyState = useMemo(() => {

    return mappedPresets.length === 0;

  }, [mappedPresets]);

  return (
    <Dialog open={props.open} onClose={handleClose} fullWidth maxWidth="lg">
      <DialogTitle className="flex-in-between">
        <Typography variant="body1" fontSize="20px">{translate('resources.security-inventory-categories.text.add_from_presets_title')}</Typography>
        <IconButton onClick={handleClose}><Close /></IconButton>
      </DialogTitle>
      <DialogContent>
        <Grid
          display="flex"
          flexDirection="row"
          gap={2}
          justifyContent="space-between"
          marginTop={2}
        >
          <Grid item xs={12}>
            <DialogContentText>{translate('resources.security-inventory-categories.text.add_from_presets_subtitle')}</DialogContentText>
          </Grid>
          <Grid item xs={8}>
            <SearchInput
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              placeholder={translate('resources.security-inventory-categories.presets.text.search')}/>
          </Grid>
        </Grid>
        {showEmptyState && (
          <Typography variant="body1" sx={{ textAlign: 'center', marginTop: '20px' }}>
            {translate('resources.security-inventory-categories.text.no_results')}
          </Typography>
        )}
        {!showEmptyState && (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  <TableCell>{translate('general.fields.name')}</TableCell>
                  <TableCell>{translate('resources.security-inventory-categories.fields.icon')}</TableCell>
                  <TableCell>{translate('resources.security-inventory-categories.fields.inspection_period')}</TableCell>
                  <TableCell>{translate('general.fields.description')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {mappedPresets.map((row) => {

                  const isSelected = selectedItems.includes(row.presetKey);
                  const isDisabled = row.exists;
                  const textColor = isDisabled ? 'text.disabled' : 'text.primary';

                  return (
                    <Tooltip title={isDisabled ? translate('resources.security-inventory-categories.text.preset_already_exists') : ''}>
                      <TableRow key={row.presetKey} onClick={() => handleToggleSelect(row.presetKey, isDisabled)} hover selected={isSelected}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={isSelected}
                            value={row.presetKey}
                            disabled={isDisabled}
                          />
                        </TableCell>
                        <TableCell sx={{ color: textColor }}>{translate(`${PRESET_I18N_PREFIX}.${row.presetKey}.name`)}</TableCell>
                        <TableCell><IconField source="icon" iconName={row.icon} /></TableCell>
                        <TableCell sx={{ color: textColor }}>
                          {row.expiryInterval.value} {translate(`resources.report-triggers.text.granularity_types.${row.expiryInterval.type}`).toLowerCase()}
                        </TableCell>
                        <TableCell sx={{ color: textColor }}>{translate(`${PRESET_I18N_PREFIX}.${row.presetKey}.description`)}</TableCell>
                      </TableRow>
                    </Tooltip>
                  );

                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button label="general.text.add" size="medium" onClick={handleAdd}
          startIcon={isLoading ? <CircularProgress color='inherit' size={20} /> : undefined} disabled={selectedItems.length === 0 || isLoading} />
      </DialogActions>
    </Dialog>
  );

};

const inventoryCategoryTransformer = (data: any) => {

  return {
    ...data,
    notifiedPeople: {
      peopleToNotify: (data.notifiedPeople?.peopleToNotify ?? []).map((personId: string) => ({
        _id: personId,
        _ref: 'Asset',
      })),
      emails: data.notifiedPeople?.emails ?? [],
    },
  };

};

const InventoryCategoryFields = () => {

  return (
    <>
      <GrayTextInputWithUpperLabel
        source="name"
        validate={required()}
        label="general.fields.name"
        helperText="resources.security-inventory-categories.text.name_helper"
        sx={{ width: '50%' }}
        FormHelperTextProps={{
          sx: {
            margin: 0,
            marginLeft: 0,
          },
        }}
      />
      <IconSelector
        source="icon"
        label="resources.security-inventory-categories.fields.icon"
        validate={required()}
      />
      <NotifiedPeopleInput
        label="resources.security-inventory-categories.fields.people_to_notify"
        helperText="resources.security-inventory-categories.text.people_to_notify_helper"
      />
      <CustomReminderPeriodInput helperText="resources.security-inventory-categories.text.reminder_periods_helper"/>
      <GrayExpiryTimeGranularityInputWithUpperLabel
        source="expiryInterval"
        label="resources.security-inventory-categories.fields.inspection_period"
        validate={required()}
        helperText="resources.security-inventory-categories.text.inspection_period_helper"
      />
      <GrayTextInputWithUpperLabel
        source="description"
        label="general.fields.description"
        multiline rows={4}
        sx={{ width: '50%' }}
      />
    </>
  );

};

const InventoryCategoryFormToolbar = () => {

  const navigate = useNavigate();

  return (
    <Toolbar sx={{ justifyContent: 'space-between', maxWidth: '1000px' }}>
      <Button label='ra.action.back' onClick={() => navigate(-1)} size="medium" sx={{ marginRight: '10px' }} variant="text"/>
      <SaveButton />
    </Toolbar>
  );

};

export const InventoryCategoryCreate = () => {

  const translate = useTranslate();
  const toastLabel = `${translate('resources.security-inventory-categories.text.title_single')} ${translate('general.text.saved').toLowerCase()}`;

  return (
    <CreateWithSuccess
      transform={inventoryCategoryTransformer}
      title="resources.security-inventory-categories.title"
      toastLabel={toastLabel}
    >
      <SimpleForm toolbar={ <InventoryCategoryFormToolbar/> } sx={{ maxWidth: '1000px' }}>
        <h4>{translate('resources.security-inventory-categories.text.create_title')}</h4>
        <Typography variant="body1">{translate('resources.security-inventory-categories.text.category_explanation')}</Typography>
        <hr/>
        <InventoryCategoryFields/>
      </SimpleForm>
    </CreateWithSuccess>
  );

};

const InventoryCategoryDeleteButton = () => {

  const translate = useTranslate();
  const record = useRecordContext();
  return (
    <DeleteButton
      mutationMode="pessimistic"
      confirmTitle={
        `${translate('ra.action.delete')} ${translate('resources.security-inventory-categories.text.title_single').toLowerCase()} "${record?.name}"`}
    />
  );

};

const InventoryItemsCountField = (props: {
  countsByCategoryId: Record<string, InventoryCategoryCounts>;
  label?: string;
}) => {

  const record = useRecordContext();
  const translate = useTranslate();
  const theme = useTheme();
  const counts = props.countsByCategoryId[record?._id ?? ''] ?? {
    validWithExpiring: 0, expiring: 0, expired: 0,
  };

  const bracketParts: React.ReactNode[] = [];
  if (counts.expiring > 0) {

    bracketParts.push(
      <span key="expiring" style={{ color: theme.palette.warning.main }}>
        {counts.expiring} {translate('resources.certification-categories.text.expiring')}
      </span>,
    );

  }

  if (counts.expired > 0) {

    bracketParts.push(
      <span key="expired" style={{ color: theme.palette.error.main }}>
        {counts.expired} {translate('resources.certification-categories.text.expired')}
      </span>,
    );

  }

  return (
    <span>
      {counts.validWithExpiring}
      {bracketParts.length > 0 && (
        <>
          {' ('}
          {bracketParts.map((part, index) => (
            <span key={index}>
              {index > 0 && ', '}
              {part}
            </span>
          ))}
          {')'}
        </>
      )}
    </span>
  );

};

const InventoryCategoriesDatagrid = () => {

  const { data: categories = [] } = useListContext();
  const { data: items = [] } = useGetList(securityItemsResource, {
    pagination: { page: 1, perPage: -1 },
    sort: { field: 'name', order: 'ASC' },
    filter: {},
  });

  const reminderDaysByCategoryId = useMemo(() => {

    return Object.fromEntries(
      categories.map((category) => [
        category._id,
        category.largestReminderPeriodInDays ?? 0,
      ]),
    );

  }, [categories]);

  const countsByCategoryId = useMemo(() => {

    return items.reduce((counts: Record<string, InventoryCategoryCounts>, item) => {

      const categoryId = item.category._id;
      const current = counts[categoryId] ?? { validWithExpiring: 0, expiring: 0, expired: 0 };

      const status = getExpiryStatus(
        item.expiryDate,
        reminderDaysByCategoryId[categoryId] ?? 0,
      );
      if (status === 'valid') current.validWithExpiring += 1;
      if (status === 'expiring') {

        current.validWithExpiring += 1;
        current.expiring += 1;

      }
      if (status === 'expired') current.expired += 1;
      counts[categoryId] = current;
      return counts;

    }, {});

  }, [items, reminderDaysByCategoryId]);
  return (
    <Datagrid rowClick={false} bulkActionButtons={false}>
      <TextField source="name" label="general.fields.name" />
      <InventoryItemsCountField
        countsByCategoryId={countsByCategoryId}
        label="resources.security-inventory-categories.fields.items_count"
      />
      <IconField source="icon" sortable={false} />
      <TimeGranularityField source="expiryInterval" label="resources.security-inventory-categories.fields.inspection_period"/>
      <AssetsInDialogButton label="resources.security-inventory-categories.fields.people_to_notify" resource="security-inventory-categories"
        source="notifiedPeople.peopleToNotify" />
      <EditButton resource="security-inventory-categories"/>
      <InventoryCategoryDeleteButton/>
    </Datagrid>
  );

};

export const InventoryCategoriesList = () => {

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createFromPresetsDialogOpen, setCreateFromPresetsDialogOpen] = useState(false);
  const translate = useTranslate();
  const redirect = useRedirect();
  const resource = useResourceContext();

  return (
    <>
      <h3>{translate('resources.security-inventory-categories.title')}</h3>
      <p>{translate('resources.security-inventory-categories.text.sub_title')}</p>
      <ListLive
        filters={SearchFields}
        debounce={LIST_DEBOUNCE}
        empty={false}
        actions={
          <IfCanAccess action="create">
            <TopToolbar>
              <Button label="general.text.add" variant="contained" size="medium" startIcon={<AddIcon/>} onClick={() => setCreateDialogOpen(true)} />
            </TopToolbar>
          </IfCanAccess>
        }>
        <InventoryCategoriesDatagrid/>
      </ListLive>
      <Toolbar sx={{ justifyContent: 'flex-start', backgroundColor: 'white' }}>
        <Button label="ra.action.back" onClick={() => redirect('list', 'security-inventory-items')} size="medium" variant="text"/>
      </Toolbar>
      <AddCategoryDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onAddFromPresets={() => {

          setCreateFromPresetsDialogOpen(true);
          setCreateDialogOpen(false);

        }}
        onAddManually={() => redirect('create', resource)}
        dialogTitle="resources.security-inventory-categories.text.add_category_dialog_title"
      />
      <InventoryCategoryCreateFromPresetsDialog open={createFromPresetsDialogOpen} onClose={() => setCreateFromPresetsDialogOpen(false)}/>
    </>
  );

};

const InventoryCategoryEditForm = () => {

  const translate = useTranslate();
  const record = useRecordContext();

  return (
    <>
      <h4 style={{ marginBottom: '10px' }}>"{record?.name}" {translate('general.text.settings')}</h4>
      <Typography variant="body1">{translate('resources.security-inventory-categories.text.settings_sub_title')}</Typography>
      <hr/>
      <InventoryCategoryFields/>
    </>
  );

};

export const InventoryCategoryEdit = () => {

  const translate = useTranslate();

  return (
    <>
      <EditWithSuccess
        transform={inventoryCategoryTransformer}
        title="resources.security-inventory-categories.title"
        actions={false}
        toastLabel={`${translate('resources.security-inventory-categories.text.title_single')} ${translate('general.text.updated').toLowerCase()}`}
      >
        <SimpleForm sx={{ maxWidth: '1000px' }} toolbar={<InventoryCategoryFormToolbar/>}>
          <InventoryCategoryEditForm/>
        </SimpleForm>
      </EditWithSuccess>
    </>
  );

};
